import React, { useContext, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { deviceType, getUA, isDesktop, isAndroid, isIOS, osName } from 'react-device-detect';
import 'dotenv/config';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../config';
import CookieBar from '../components/CookieBar';

const sanctionedList = [
  'AF', // Afghanistan
  'BY', // Belarus
  'CN', // China
  'CU', // Cuba
  'IR', // Iran,
  'JP', // Japan,
  'LY', // Libya,
  'KP', // North Korea,
  'RU', // Russia,
  'KR', // South Korea,
  'SD', // Sudan,
  'SY' // Syria
];

export const TrackingContext = React.createContext();
export const useTrackingContext = () => useContext(TrackingContext);

export const TrackingProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [internal, setInternal] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [alert, setAlert] = useState(false);
  const [age, setAge] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [dob, setDob] = useState(null);
  const [utmData, setUtmData] = useState({});

  const [formCountry, setFormCountry] = useState(null);
  const [language, setLanguage] = useState(null);
  const [sessionTrackingId, setSessionTrackingId] = useState(uuidv4());
  const [evId, setEvId] = useState(new URLSearchParams(location.search).get('evId'));

  const TRACKING_USER_ID = '_barbUserId';
  const TRACKING_EXPIRED_DAYS = 30;

  const TRACKING_URL = config.everywhereApi + '/api/track';

  const getLanguage = () => Intl.DateTimeFormat().resolvedOptions().locale || 'en-GB';

  const shouldTrack = () => {
    const allowTracking = localStorage.getItem('acceptCookie') === '1';

    return config.eventTrackingEnabled && allowTracking;
  };

  const constructEventPayload = async (type, userId, platform, timestamp, data) => {
    const language = getLanguage();

    let eventProp = {
      country: country || (await lookupCountry()),
      event_type: type,
      platform,
      user_id: userId,
      source: 'BARB',
      timestamp,
      tracking: true,
      ...utmData
    };

    switch (type) {
      case 'page_view':
        eventProp = {
          ...eventProp,
          referrer: document.referrer,
          screen_resolution: `${window.innerWidth}x${window.innerHeight}`,
          user_agent: getUA,
          page_name: data.pageName,
          account_creation_step: data.accountCreationStep,
          is_legacy: !!data.legacyId,
          language
        };

        break;
      case 'click':
        eventProp = {
          ...eventProp,
          button_name: data.btnName,
          accepted: data.accepted,
          account_creation_step: data.accountCreationStep,
          builder_preferences: data.builderPreferences,
          language
        };

        break;
      case 'video_start':
        eventProp = {
          ...eventProp,
          video_provider: data.videoProvider,
          video_title: data.videoTitle,
          video_url: data.videoUrl
        };
        break;
      case 'form_start':
        eventProp = {
          ...eventProp,
          form_id: data.formId,
          form_name: data.formName,
          form_destination: data.formDestination,
          account_creation_step: data.accountCreationStep
        };
        break;
      case 'form_submit':
        eventProp = {
          ...eventProp,
          form_id: data.formId,
          age_group: data.ageGroup,
          email_signup: data.emailSignup,
          trust_ip: data.trustIp,
          form_name: data.formName,
          form_destination: data.formDestination,
          account_creation_step: data.accountCreationStep,
          referral_id: data.referralId
        };

        break;
      case 'error':
        eventProp = {
          event_type: type,
          platform,
          userId,
          timestamp,
          error_id: data.errorId,
          error_code: data.errorCode,
          error_text: data.errorText
        };
        break;
      default:
      // do nothing
    }

    return eventProp;
  };

  const createTrackingUserId = (existing) => {
    const newUserId = existing || sessionTrackingId;

    if (shouldTrack()) {
      Cookies.set(TRACKING_USER_ID, newUserId, { expires: TRACKING_EXPIRED_DAYS });
    }

    return newUserId;
  };

  const getPlatform = () => {
    let platform;
    if (isDesktop) {
      platform = osName;
    } else if (isAndroid) {
      platform = 'Android';
    } else if (isIOS) {
      platform = 'iOS';
    }
    return platform || deviceType;
  };

  const getTrackingUserId = () => {
    const userId = Cookies.get(TRACKING_USER_ID);

    if (evId) {
      return evId === userId ? userId : createTrackingUserId(evId);
    }

    if (userId) return userId;

    return createTrackingUserId();
  };

  const declineCookie = () => {
    if (evId) {
      setEvId(null);
    }
  };

  const trackEvent = async (type, data = {}) => {
    if (sanctionedList.includes(country)) {
      navigate('/blocked');
      return;
    }

    if (!shouldTrack()) {
      return;
    }

    const platform = getPlatform();
    const timestamp = new Date().toISOString();
    const userId = getTrackingUserId();

    const payload = {
      data: await constructEventPayload(type, userId, platform, timestamp, data)
    };

    const headers = new Headers({
      'Content-Type': 'application/json',
      ...(Cookies.get('_barbToken') && { Authorization: 'Bearer ' + Cookies.get('_barbToken') })
    });

    try {
      await fetch(TRACKING_URL, {
        method: 'post',
        headers,
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error(err);
    }
  };

  const lookupCountry = async () => {
    try {
      const response = await fetch(TRACKING_URL);

      const res = await response.json();

      setCountry(res.countryIsoCode);
      return res.countryIsoCode;
    } catch (err) {
      console.error(err);
    }
  };

  const trackPageView = (pageInfo) => trackEvent('page_view', pageInfo);

  const trackClick = (clickInfo) => trackEvent('click', clickInfo);

  const trackVideoStart = (videoInfo) => trackEvent('video_start', videoInfo);

  const trackFormStart = (formInfo) => trackEvent('form_start', formInfo);

  const trackFormSubmit = (formInfo) => trackEvent('form_submit', formInfo);

  const trackError = (errorDetail) => trackEvent('error', errorDetail);

  const trackSignUp = () => {
    if (!shouldTrack()) {
      return;
    }

    // Stackadapt
    if (window.saq) {
      window.saq('conv', config.stackAdaptId);
    }

    // Tiktok
    if (window.ttq) {
      window.ttq.track('Subscribe', { event: 'Account Creation Verification Page' });
    }

    // Meta
    if (window.fbq) {
      window.fbq('track', 'Subscribe', { event: 'Account Creation Verification Page' });
    }

    // Google Tag
    if (window.gtag) {
      window.gtag('event', 'conversion', { send_to: 'AW-11229548490/HinHCIfOw7kZEMqf1eop' });
    }

    // Reddit
    if (window.rdt) {
      window.rdt('track', 'SignUp');
    }
  };

  const trackAccountCreate = (alpha) => {
    if (!shouldTrack()) {
      return;
    }

    // Stackadapt
    if (window.saq) {
      window.saq('conv', alpha ? 'UxTqzfMfywBLbrOah5RROL' : 'JdFD3D4Fz6q0VEHmZwYGJY');
    }

    // Tiktok
    if (window.ttq) {
      window.ttq.track('CompleteRegistration', {
        event: alpha
          ? 'Account Creation Conversion - Closed Alpha Yes'
          : 'Account Creation Conversion - Closed Alpha No'
      });
    }

    // Meta
    if (window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        event: alpha
          ? 'Account Creation Conversion - Closed Alpha Yes'
          : 'Account Creation Conversion - Closed Alpha No'
      });
    }

    // Google Tag
    if (window.gtag) {
      window.gtag('event', 'conversion', { send_to: 'AW-11229548490/ywSSCLXIwLkZEMqf1eop' });
    }

    // Reddit
    if (window.rdt) {
      window.rdt('track', 'Custom', { customEventName: 'SignUp-Confirmation' });
    }
  };

  const trackReject = async () => {
    const eventProp = {
      event_type: 'click',
      button_name: 'Reject',
      source: 'BARB'
    };

    const payload = {
      data: eventProp
    };

    const headers = new Headers({
      'Content-Type': 'application/json'
    });

    try {
      await fetch(TRACKING_URL, {
        method: 'post',
        headers,
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error(`trackReject error: ${err}`);
    }
  };

  const trackShowConsentModal = async () => {
    const eventProp = {
      event_type: 'showConsent',
      source: 'BARB'
    };

    const payload = {
      data: eventProp
    };

    const headers = new Headers({
      'Content-Type': 'application/json'
    });

    try {
      await fetch(TRACKING_URL, {
        method: 'post',
        headers,
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error(`trackShowConsentModal error: ${err}`);
    }
  };

  const onAccept = (isAccepted) => {
    setTracking(isAccepted);
  };

  const siteVisit = async () => {
    if (!shouldTrack()) {
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const timestamp = new Date().toISOString();

    const userId = getTrackingUserId();

    const eventProp = {
      timestamp,
      event_type: 'website_visit',
      source: 'BARB',
      tracking: shouldTrack(),
      userId,
      utm_content: queryParams.get('utm_content') || '',
      utm_source: queryParams.get('utm_source') || '',
      utm_medium: queryParams.get('utm_medium') || '',
      utm_campaign: queryParams.get('utm_campaign') || '',
      utm_country: queryParams.get('utm_country') || ''
    };

    setUtmData({
      utm_content: queryParams.get('utm_content') || '',
      utm_source: queryParams.get('utm_source') || '',
      utm_medium: queryParams.get('utm_medium') || '',
      utm_campaign: queryParams.get('utm_campaign') || '',
      utm_country: queryParams.get('utm_country') || ''
    });

    const payload = {
      data: eventProp
    };

    const headers = new Headers({
      'Content-Type': 'application/json',
      ...(Cookies.get('_barbToken') && { Authorization: 'Bearer ' + Cookies.get('_barbToken') })
    });

    try {
      await fetch(TRACKING_URL, {
        method: 'post',
        headers,
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error(`siteVisit error: ${err}`);
    }
  };

  useEffect(() => {
    siteVisit();
  }, []);

  useEffect(() => {
    setTracking(shouldTrack());

    if (tracking) {
      // Tiktok
      if (!window.ttq) {
        const tiktok = (w, d, t) => {
          w.TiktokAnalyticsObject = t;
          const ttq = (w[t] = w[t] || []);
          ttq.methods = [
            'page',
            'track',
            'identify',
            'instances',
            'debug',
            'on',
            'off',
            'once',
            'ready',
            'alias',
            'group',
            'enableCookie',
            'disableCookie'
          ];
          ttq.setAndDefer = function (t, e) {
            t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            };
          };
          for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
          ttq.instance = function (t) {
            for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
              ttq.setAndDefer(e, ttq.methods[n]);
            return e;
          };
          ttq.load = function (e, n) {
            const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
            ttq._i = ttq._i || {};
            ttq._i[e] = [];
            ttq._i[e]._u = i;
            ttq._t = ttq._t || {};
            ttq._t[e] = +new Date();
            ttq._o = ttq._o || {};
            ttq._o[e] = n || {};
            n = document.createElement('script');
            n.type = 'text/javascript';
            n.async = !0;
            n.src = i + '?sdkid=' + e + '&lib=' + t;
            e = document.getElementsByTagName('script')[0];
            e.parentNode.insertBefore(n, e);
          };
          ttq.load('CGCPE5BC77UD9R2716I0');
          ttq.page();
        };
        tiktok(window, document, 'ttq');
      }

      // Meta
      if (!window.fbq) {
        const MetaTracking = (f, b, e, v, n, t, s) => {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = !0;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        };
        MetaTracking(
          window,
          document,
          'script',
          'https://connect.facebook.net/en_US/fbevents.js',
          null,
          null,
          null
        );
        window.fbq('init', '639890544256968');
        window.fbq('track', 'PageView');
      }

      //StackAdapt
      if (!window.saq) {
        const StackAdapt = (s, a, e, v, n, t, z) => {
          if (s.saq) return;
          n = s.saq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!s._saq) s._saq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '1.0';
          n.queue = [];
          t = a.createElement(e);
          t.async = !0;
          t.src = v;
          z = a.getElementsByTagName(e)[0];
          z.parentNode.insertBefore(t, z);
        };
        StackAdapt(window, document, 'script', config.stackAdapt.url);
        window.saq('ts', '7HbRL2XpwXdtRfpyeC_ycQ');
      }

      //Reddit
      if (!window.rdt) {
        const Reddit = (w, d) => {
          if (!w.rdt) {
            var p = (w.rdt = function () {
              p.sendEvent ? p.sendEvent.apply(p, arguments) : p.callQueue.push(arguments);
            });
            p.callQueue = [];
            var t = d.createElement('script');
            t.src = 'https://www.redditstatic.com/ads/pixel.js';
            t.async = !0;
            var s = d.getElementsByTagName('script')[0];
            s.parentNode.insertBefore(t, s);
          }
        };
        Reddit(window, document);

        window.rdt('init', 'a2_f2bh6vc85erc');
      }
    }
  }, [tracking]);

  return (
    <TrackingContext.Provider
      value={{
        accountCreated,
        setAccountCreated,
        alert,
        setAlert,
        dob,
        setDob,
        language,
        setLanguage,
        formCountry,
        country,
        setFormCountry,
        email,
        setEmail,
        internal,
        setInternal,
        age,
        setAge,
        tracking,
        setTracking,
        lookupCountry,
        trackPageView,
        trackClick,
        trackVideoStart,
        trackFormStart,
        trackFormSubmit,
        trackError,
        trackSignUp,
        trackReject,
        trackShowConsentModal,
        trackAccountCreate,
        declineCookie,
        utmData
      }}>
      <CookieBar handleAccept={onAccept} createTrackingUserId={createTrackingUserId} />
      {children}
    </TrackingContext.Provider>
  );
};
